import {Game} from "../Game";
import {ISimplePresentation, ISimplePresentationRunnerController} from "../SimplePresentationRunner";
import {gsap} from "gsap";
import {IFeatureData} from "../server/IData";
import {GEStates, Symbol} from "../reels/Symbol";
import {Sound} from "../sound/Sound";

export class GEBombRunner implements ISimplePresentation {

    public shouldRun(): boolean {
        return Game.model.gameData.gridExpanderDirections && Game.model.gameData.gridExpanderDirections.length > 0 && !Game.model.gameData.maxExpansion && !Game.model.gameData.suspendedAction;
    }

    public start(controller: ISimplePresentationRunnerController): void {
        const tl: gsap.core.Timeline = gsap.timeline({
            onComplete: () => {
                controller.finish();
            }
        });

        tl.add(() => {
            controller.setSkipCallback(() => {
                this.onSkip(tl);
            });
        });

        tl.add(this.performGE());
    }

    private performGE(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        const gESymbols: IFeatureData[] = Game.model.gEData;
        const gridExpDirections: number[][] = Game.model.gameData.gridExpanderDirections;
        let expSymbolPos: IFeatureData[] = [];
        let activeReels: number = Game.ACTIVE_REELS_NUM,
            activeRows: number = Game.ACTIVE_ROWS_NUM;
        const prevActiveReels: number = Game.ACTIVE_REELS_NUM,
            prevActiveRows: number = Game.ACTIVE_ROWS_NUM;

        gESymbols.forEach((gEItem: IFeatureData, idx: number) => {
            this.prepareExpSymData(gEItem.reelId, gEItem.rowId, gridExpDirections[idx], expSymbolPos);
        });

        expSymbolPos.forEach(({reelId, rowId}: IFeatureData) => {
            const reelsSymName: string = Game.model.gameData.evaluatedArea[reelId][rowId],
                sym: Symbol = Game.reelsView.reelModels[reelId].getSymbolAt(rowId, reelsSymName);

            if (sym) {
                tl.add(Game.symbolFloors.playExplodePatternHighlightAnimation(reelId, rowId), 0);
            }
        });

        gESymbols.forEach((gEItem: IFeatureData, idx: number) => {
            const gESymbol: Symbol = Game.reelsView.reelModels[gEItem.reelId].getSymbolAt(gEItem.rowId, "GE");

            if (gESymbol) {
                const animName: string = gridExpDirections[idx][0] === 0 ? GEStates.TRIGGER_VER : GEStates.TRIGGER_HOR;

                gESymbol.isRemoved = true;

                tl.add([
                    () => {
                        if (gESymbol.parent) {
                            gESymbol.parent.addChild(gESymbol);
                        }
                    },
                    gESymbol.getStateAnimation(animName),
                    Game.overlaysForSymbols.playEffectAnimationGE(gESymbol, animName)
                ], (5 / 30)); // #135

                tl.add(Game.symbolFloors.showExplosionMark(gEItem.reelId, gEItem.rowId), "<0.2");
            }
        });

        tl.add(() => {
            Game.soundPlayer.playSound(Sound.GE_EXPLODE, 1);
        }, (5 / 30));

        tl.addLabel("attentionCompleted", (20 / 30));

        expSymbolPos.forEach(({reelId, rowId}: IFeatureData) => {
            tl.add(Game.removeSymbolFromAReel(reelId, rowId), "attentionCompleted");
        });

        gESymbols.forEach((gEItem: IFeatureData, idx: number) => {
            const gESymbol: Symbol = Game.reelsView.reelModels[gEItem.reelId].getSymbolAt(gEItem.rowId, "GE");
            let extraReels: number = 0, extraRows: number = 0, addReelsToRight: boolean = false,
                addRowsInBottom: boolean = false;

            if (gESymbol) {
                extraReels = (gridExpDirections[idx][0] === 0 ? 0 : (activeReels === 8 ? 1 : 2));
                extraRows = (gridExpDirections[idx][1] === 0 ? 0 : (activeRows === 8 ? 1 : 2));
                addReelsToRight = extraReels > 0 && gridExpDirections[idx][0] === 1;
                addRowsInBottom = extraRows > 0 && gridExpDirections[idx][1] === 1;

                const {
                    leftCRReelId,
                    leftCRRowId,
                    rightCRReelId,
                    rightCRRowId
                } = Game.cornerRemovalUtils(extraReels, extraRows, addReelsToRight, addRowsInBottom);

                activeReels += extraReels;
                activeRows += extraRows;

                tl.add([
                    Game.handleCornerRemoval(leftCRReelId, leftCRRowId, rightCRReelId, rightCRRowId),
                    Game.expandGrid(extraReels, extraRows, addReelsToRight, addRowsInBottom)
                ], "attentionCompleted");

                tl.add(this.showGEExplodePatternAfterExpansion(gEItem.reelId, gEItem.rowId, addReelsToRight, addRowsInBottom, prevActiveReels, prevActiveRows), "attentionCompleted");
            }
        });

        tl.add(()=>{
            Game.shakeScreen()
        }, "-=0.8");

        return tl;
    }

    private showGEExplodePatternAfterExpansion(targetReelId: number, targetRowId: number, addReelsToRight: boolean, addRowsInBottom: boolean, prevReelsNum: number, prevRowsNum: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        
        tl.add(() => {
            Game.soundPlayer.playSound(Sound.ATTACK_HIGHLIGHT, 1)
        });

        if (!(prevReelsNum === Game.TOTAL_REELS_NUM && prevRowsNum === Game.TOTAL_REELS_NUM)){
            if (Game.ACTIVE_REELS_NUM > prevReelsNum){
                if (addReelsToRight){
                    for (let i=prevReelsNum-1; i<Game.ACTIVE_REELS_NUM; i++){
                        for (let j=targetRowId-1; j<=targetRowId+1 && j<Game.ACTIVE_ROWS_NUM; j++){
                            if (j<0){
                                continue;
                            }

                            if (i===(prevReelsNum-1)){
                                tl.add(Game.showExplodePatternSparkOnSymbols(i, j, j+1), 0);
                            } else{
                                tl.add([
                                    Game.showExplodePatternHighlighterOnSymbols(i, j, j+1),
                                    Game.showExplodePatternSparkOnSymbols(i, j, j+1)
                                ], 0);
                            }
                        }
                    }
                } else{
                    for (let i=0; i<Game.ACTIVE_REELS_NUM-prevReelsNum+1; i++){
                        for (let j=targetRowId-1; j<=targetRowId+1 && j<Game.ACTIVE_ROWS_NUM; j++){
                            if (j<0){
                                continue;
                            }

                            if (i===(Game.ACTIVE_REELS_NUM-prevReelsNum)){
                                tl.add(Game.showExplodePatternSparkOnSymbols(i, j, j+1), 0);
                            } else{
                                tl.add([
                                    Game.showExplodePatternHighlighterOnSymbols(i, j, j+1),
                                    Game.showExplodePatternSparkOnSymbols(i, j, j+1)
                                ], 0);
                            }
                        }
                    }
                }
            } else if (Game.ACTIVE_ROWS_NUM > prevRowsNum){
                if (addRowsInBottom){
                    for (let i=prevRowsNum-1; i<Game.ACTIVE_ROWS_NUM; i++){
                        for (let j=targetReelId-1; j<=targetReelId+1 && j<Game.ACTIVE_REELS_NUM; j++){
                            if (j<0){
                                continue;
                            }
                            if (i===(prevRowsNum-1)){
                                tl.add(Game.showExplodePatternSparkOnSymbols(j, i, i+1), 0);
                            } else{
                                tl.add([
                                    Game.showExplodePatternHighlighterOnSymbols(j, i, i+1),
                                    Game.showExplodePatternSparkOnSymbols(j, i, i+1)
                                ], 0);
                            }
                        }
                    }
                } else{
                    for (let i=-1; i<Game.ACTIVE_ROWS_NUM-prevRowsNum; i++){
                        for (let j=targetReelId-1; j<=targetReelId+1 && j<Game.ACTIVE_REELS_NUM; j++){
                            if (j<0){
                                continue;
                            }
                            if (i===-1){
                                tl.add(Game.showExplodePatternSparkOnSymbols(j, i, i+1), 0);
                            } else{
                                tl.add([
                                    Game.showExplodePatternHighlighterOnSymbols(j, i, i+1),
                                    Game.showExplodePatternSparkOnSymbols(j, i, i+1)
                                ], 0);
                            }
                        }
                    }
                }
            }
        }

        return tl;
    }

    private prepareExpSymData(reelId: number, rowId: number, direction: number[], expSymbolPos: IFeatureData[]): void {
        if (direction[0] !== 0) { // horizontal explosion (along the row)
            for (let i: number = 0; i < Game.ACTIVE_REELS_NUM; i++) {
                for (let j: number = rowId - 1; j <= rowId + 1; j++) {
                    if (j > -1 && j < Game.ACTIVE_ROWS_NUM) {
                        expSymbolPos.push({reelId: i, rowId: j});
                    }
                }
            }
        } else if (direction[1] !== 0) { // vertical explosion (along the reel)
            for (let i: number = reelId - 1; i <= reelId + 1; i++) {
                if (i > -1 && i < Game.ACTIVE_REELS_NUM) {
                    for (let j: number = 0; j < Game.ACTIVE_ROWS_NUM; j++) {
                        expSymbolPos.push({reelId: i, rowId: j});
                    }
                }
            }
        }
    }

    private onSkip(tl: gsap.core.Timeline): void {
        tl.totalProgress(1);
        Game.soundPlayer.stopSound(Sound.GE_EXPLODE);
    }
}